import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useAuth, API } from '@/App';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, Bell, Lock, Shield, AlertTriangle } from 'lucide-react';
import { usePageMeta } from '@/hooks/usePageMeta';

const TalentSettings = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [notifications, setNotifications] = useState({
    projectUpdates: true,
    applicationStatus: true,
    weeklyDigest: false,
    marketingEmails: false,
  });

  usePageMeta({
    title: 'Settings | Talent Collective',
    description: 'Manage your account settings and preferences.'
  });

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
      });
      toast.success('Password updated successfully.');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update password.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      try {
        await axios.delete(`${API}/auth/account`);
        toast.success('Account deleted.');
        logout();
        navigate('/');
      } catch (error) {
        toast.error('Failed to delete account.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="settings-page">
      <Header />

      <div className="container-custom py-12 max-w-3xl">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center text-sm text-slate-500 hover:text-slate-700 mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Dashboard
        </button>

        <div className="mb-8">
          <h1 className="heading-2 mb-2">Settings</h1>
          <p className="text-slate-600">Manage your account preferences.</p>
        </div>

        <div className="space-y-6">
          {/* Notifications */}
          <Card className="p-8" data-testid="notifications-card">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-10 w-10 rounded-xl bg-teal-50 flex items-center justify-center">
                <Bell className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <h2 className="heading-4">Notifications</h2>
                <p className="text-sm text-slate-500">Manage how you receive updates.</p>
              </div>
            </div>

            <div className="space-y-4">
              {[
                { key: 'projectUpdates', label: 'Project Updates', desc: 'New projects matching your profile.' },
                { key: 'applicationStatus', label: 'Application Status', desc: 'Status changes on your applications.' },
                { key: 'weeklyDigest', label: 'Weekly Digest', desc: 'A weekly summary of new opportunities.' },
                { key: 'marketingEmails', label: 'Community Updates', desc: 'News and stories from the ACT community.' },
              ].map((item) => (
                <div key={item.key} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
                  <div>
                    <p className="font-medium text-slate-800">{item.label}</p>
                    <p className="text-sm text-slate-500">{item.desc}</p>
                  </div>
                  <Switch
                    checked={notifications[item.key]}
                    onCheckedChange={(checked) => setNotifications({
                      ...notifications,
                      [item.key]: checked
                    })}
                  />
                </div>
              ))}
            </div>
          </Card>

          {/* Password */}
          <Card className="p-8" data-testid="password-card">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-10 w-10 rounded-xl bg-teal-50 flex items-center justify-center">
                <Lock className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <h2 className="heading-4">Change Password</h2>
                <p className="text-sm text-slate-500">Update your account password.</p>
              </div>
            </div>

            <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
              <div className="space-y-2">
                <Label htmlFor="currentPassword">Current Password</Label>
                <Input
                  id="currentPassword"
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  required
                  data-testid="current-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  required
                  data-testid="new-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  required
                  data-testid="confirm-password-input"
                />
              </div>
              <Button type="submit" className="btn-primary" disabled={loading} data-testid="update-password-btn">
                {loading ? 'Updating...' : 'Update Password'}
              </Button>
            </form>
          </Card>

          {/* Account Info */}
          <Card className="p-8" data-testid="account-info-card">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center">
                <Shield className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <h2 className="heading-4">Account</h2>
                <p className="text-sm text-slate-500">Your account details.</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex justify-between text-sm border-b border-slate-100 pb-4">
                <span className="text-slate-500">Email</span>
                <span className="text-slate-900 font-medium">{user?.email}</span>
              </div>
              <div className="flex justify-between text-sm border-b border-slate-100 pb-4">
                <span className="text-slate-500">Role</span>
                <span className="text-slate-900 font-medium capitalize">{user?.role}</span>
              </div>
            </div>
          </Card>

          {/* Danger Zone */}
          <Card className="p-8 border-red-200" data-testid="danger-zone">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-xl bg-red-50 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-red-500" />
              </div>
              <div>
                <h2 className="heading-4 text-red-700">Danger Zone</h2>
                <p className="text-sm text-slate-500">Irreversible actions.</p>
              </div>
            </div>

            <Button 
              variant="outline" 
              className="text-red-600 border-red-200 hover:bg-red-50" 
              onClick={handleDeleteAccount}
              data-testid="delete-account-btn"
            >
              Delete Account
            </Button>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TalentSettings;
